import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { StatsCard } from 'common/components';

import { ErrorTemplate } from './ErrorTemplate';
import { Loader } from './Loader';

/**
 * @typedef {Object} WidgetProps
 * @property {import('react').ReactNode} [actionElement]
 * @property {Function} [children]
 * @property {import('react').ReactNode} [header]
 * @property {*[]} promises
 * @property {import('react').ReactNode} [subheader]
 */

/**
 * @param {WidgetProps} props
 */
export const Widget = ({ actionElement, children, promises, subheader, header, footer }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getData = useCallback(
    async signal => {
      if (!promises || promises.length === 0) {
        setData(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await Promise.all(promises.map(promise => promise({ signal })));
        if (signal?.aborted) return;

        setData(response?.data ?? response);
      } catch (e) {
        if (axios.isCancel(e) || signal?.aborted) return;

        setError(e.message);
      }
      setIsLoading(false);
    },
    [promises]
  );

  useEffect(() => {
    const controller = new AbortController();
    getData(controller.signal);

    return () => controller.abort();
  }, [getData, promises]);

  const handleResetClick = async () => {
    setError(null);
    await getData();
  };

  return (
    <StatsCard actionElement={actionElement} header={header} subheader={subheader} footer={footer}>
      {isLoading && <Loader />}
      {error && <ErrorTemplate onReset={handleResetClick} />}
      {!isLoading && !error && data && children?.(data)}
    </StatsCard>
  );
};
